// src/services/api/auth.js
// SSE connection and tab change functions.

import {
    fetchWithCommonHeaders,
    startNetworkActivity,
    endNetworkActivity,
    API_URL,
    AuthError,
    getGlobalAuthErrorHandler,
} from './helpers';

export const connectSSE = (token, updateHandlers, onError, activeTab) => {
    if (!token) {
        const err = new AuthError('Authorization required');
        if (onError) onError(err);
        const handler = getGlobalAuthErrorHandler();
        if (handler) handler(err);
        return null;
    }

    const sseUrl = `${API_URL}v2/events?token=${encodeURIComponent(token)}&tab=${encodeURIComponent(activeTab)}`;
    const eventSource = new EventSource(sseUrl);

    eventSource.addEventListener('v3Init', (e) => {
        try {
            if (updateHandlers.v3Init) {
                updateHandlers.v3Init(e.data);
            }
        } catch (error) {
            if (onError) onError(error);
        }
    });

    eventSource.addEventListener('v3Patch', (e) => {
        try {
            if (updateHandlers.v3Patch) {
                updateHandlers.v3Patch(e.data);
            }
        } catch (error) {
            if (onError) onError(error);
        }
    });

    eventSource.addEventListener('v3Inventory', (e) => {
        try {
            if (updateHandlers.v3Inventory) {
                updateHandlers.v3Inventory(e.data);
            }
        } catch (error) {
            if (onError) onError(error);
        }
    });

    eventSource.addEventListener('v3Hero', (e) => {
        try {
            if (updateHandlers.v3Hero) {
                updateHandlers.v3Hero(e.data);
            }
        } catch (error) {
            if (onError) onError(error);
        }
    });

    eventSource.addEventListener('v3Guild', (e) => {
        try {
            if (updateHandlers.v3Guild) {
                updateHandlers.v3Guild(e.data);
            }
        } catch (error) {
            if (onError) onError(error);
        }
    });

    eventSource.addEventListener('heartbeat', (e) => {
        try {
            if (updateHandlers.heartbeat) {
                updateHandlers.heartbeat(e.data);
            }
        } catch (_) {
            // heartbeat errors are non-critical
        }
    });

    eventSource.addEventListener('v3Update', (e) => {
        try {
            const update = JSON.parse(e.data);

            if (update.tab === 'world_v3' && update.type === 'patch' && updateHandlers.v3Patch) {
                updateHandlers.v3Patch(JSON.stringify(update.data));
            } else if (update.tab === 'world_v3' && update.type === 'full' && updateHandlers.v3Init) {
                updateHandlers.v3Init(JSON.stringify(update.data));
            } else if (update.tab === 'v3_inventory' && updateHandlers.v3Inventory) {
                updateHandlers.v3Inventory(JSON.stringify(update.data));
            } else if (update.tab === 'v3_hero' && updateHandlers.v3Hero) {
                updateHandlers.v3Hero(JSON.stringify(update.data));
            } else if (update.tab === 'v3_guild' && updateHandlers.v3Guild) {
                updateHandlers.v3Guild(JSON.stringify(update.data));
            }
        } catch (error) {
            if (onError) onError(error);
        }
    });

    eventSource.onmessage = (e) => {
        try {
            if (updateHandlers.onmessage) {
                updateHandlers.onmessage(e);
                return;
            }

            startNetworkActivity(activeTab);

            if (!e.data) {
                return;
            }

            if (activeTab === 'feed' && (e.data === 'null' || e.data === '[]')) {
                if (typeof updateHandlers['_clearWidgets'] === 'function') {
                    updateHandlers['_clearWidgets']();
                }
                return;
            }

            let updates = JSON.parse(e.data);
            let dynamicBatchKeys = new Set();

            if (!Array.isArray(updates)) {
                throw new Error('Invalid data format');
            }

            if (updates.length === 0) {
                return;
            }

            updates.forEach(({ key, value }) => {
                if ((activeTab === 'Market_Deals' || activeTab === 'Market_Offers') && key === 'marketDeals') {
                    if (typeof updateHandlers['marketDeals'] === 'function') {
                        updateHandlers['marketDeals'](value);
                        dynamicBatchKeys.add(key);
                    }
                }
                else if (activeTab === 'Market_Offers' && key === 'marketOffers') {
                    if (typeof updateHandlers['marketOffers'] === 'function') {
                        updateHandlers['marketOffers'](value);
                        dynamicBatchKeys.add(key);
                    }
                }
                else if (activeTab === 'Market_Offers' && key === 'marketQuotes') {
                    if (typeof updateHandlers['marketQuotes'] === 'function') {
                        updateHandlers['marketQuotes'](value);
                        dynamicBatchKeys.add(key);
                    }
                }
                else if (activeTab === 'guild' && /^W\d+$/.test(key)) {
                    if (typeof updateHandlers['_widgetDynamic'] === 'function') {
                        updateHandlers['_widgetDynamic'](key, value);
                        dynamicBatchKeys.add(key);
                    }
                }
                else if (activeTab === 'map' && /^M\d+$/.test(key)) {
                    if (typeof updateHandlers['_widgetDynamic'] === 'function') {
                        updateHandlers['_widgetDynamic'](key, value);
                        dynamicBatchKeys.add(key);
                    }
                }
                else if (activeTab === 'area' && /^A\d+$/.test(key)) {
                    if (typeof updateHandlers['_widgetDynamic'] === 'function') {
                        updateHandlers['_widgetDynamic'](key, value);
                        dynamicBatchKeys.add(key);
                    }
                }
                else if (activeTab === 'profile' && /^P\d+$/.test(key)) {
                    if (typeof updateHandlers['_widgetDynamic'] === 'function') {
                        updateHandlers['_widgetDynamic'](key, value);
                        dynamicBatchKeys.add(key);
                    }
                }
                else if ((activeTab === 'feed' || activeTab === 'feed_v3') && /^F\d+$/.test(key)) {
                    if (typeof updateHandlers['_widgetDynamic'] === 'function') {
                        updateHandlers['_widgetDynamic'](key, value);
                        dynamicBatchKeys.add(key);
                    }
                }
                else if (key in updateHandlers) {
                    updateHandlers[key](value);
                    dynamicBatchKeys.add(key);
                }
            });

            if (typeof updateHandlers['_finishDynamicUpdate'] === 'function') {
                updateHandlers['_finishDynamicUpdate'](Array.from(dynamicBatchKeys));
            }

            setTimeout(() => {
                endNetworkActivity(activeTab);
            }, 1000);

        } catch (_) {
            // SSE message processing error — handled gracefully
        }
    };

    eventSource.onerror = (err) => {
        if (err && err.target && err.target.readyState === EventSource.CLOSED) {
            if (onError) onError(err);
            eventSource.close();
            return;
        }
        if (!token) {
            const authErr = new AuthError('Authorization required');
            if (onError) onError(authErr);
            eventSource.close();
            return;
        }
        if (onError) onError(err);
        eventSource.close();
    };

    return eventSource;
};

export const sendTabChange = async (token, activeTab) => {
    const handler = getGlobalAuthErrorHandler();
    if (!token) {
        const err = new AuthError('Authorization required');
        if (handler) handler(err);
        return null;
    }
    try {
        startNetworkActivity(activeTab);
        const response = await fetchWithCommonHeaders(
            `${API_URL}v2/tabchange`,
            {
                method: "POST",
                token,
                body: { activeTab },
            }
        );
        endNetworkActivity(activeTab);
        if (response.status === 401 || response.status === 403) {
            const err = new AuthError('Authorization error');
            if (handler) handler(err);
            return null;
        }
        if (!response.ok) {
            //  console.error('Failed to change tab:', response.statusText);
        }
    } catch (error) {
        endNetworkActivity(activeTab);
        if (error instanceof AuthError) {
            if (handler) handler(error);
            return null;
        }
        //console.error('Error changing tab:', error);
        throw error;
    }
};
